// =====================================================================
// App.js - Fixed Version
// الإصلاحات:
// 1. إضافة MaintenanceGuard - بيتحقق من الصيانة قبل ما يعرض أي صفحة
// 2. الأدمن المسجل دخوله يعدي حتى لو الصيانة شغالة
// 3. الزوار بيتحولوا لصفحة الصيانة تلقائياً
// =====================================================================

import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider }         from './contexts/ThemeContext';
import { LanguageProvider }      from './contexts/LanguageContext';

import ErrorBoundary  from './components/Common/ErrorBoundary';
import LoadingSpinner from './components/Common/LoadingSpinner';
import ChatWidget     from './components/ChatWidget';

import PublicLayout from './components/Layout/PublicLayout';
import AdminLayout  from './components/admin/AdminLayout';
import AuthLayout   from './components/Layout/AuthLayout';

import publicChatApi from './services/publicChatApi';

// ── Lazy Pages ────────────────────────────────────────────────────────
const HomePage          = React.lazy(() => import('./pages/Home/HomePage.js'));
const AboutPage         = React.lazy(() => import('./pages/About/AboutPage.jsx'));
const ProjectsPage      = React.lazy(() => import('./pages/Projects/ProjectsPage.jsx'));
const ProjectDetailsPage = React.lazy(() => import('./pages/Projects/ProjectDetailsPage.jsx'));
const BlogPage          = React.lazy(() => import('./pages/Blog/BlogPage.jsx'));
const BlogPostPage      = React.lazy(() => import('./pages/Blog/BlogPostPage.jsx'));
const ContactPage       = React.lazy(() => import('./pages/Contact/ContactPage.jsx'));
const RoleSelectPage    = React.lazy(() => import('./pages/RoleSelect.jsx'));
const MaintenancePage   = React.lazy(() => import('./pages/Maintenance/MaintenancePage.jsx'));
const AdminGatePage     = React.lazy(() => import('./pages/AdminGatePage.jsx'));
const AdminLoginPage    = React.lazy(() => import('./pages/Admin/Auth/AdminLoginPage.js'));

const DashboardOverview   = React.lazy(() => import('./pages/Admin/Dashboard/DashboardOverview.jsx'));
const Profile             = React.lazy(() => import('./pages/Admin/Profile/Profile.jsx'));
const UsersManager        = React.lazy(() => import('./pages/Admin/Users/UsersManager.jsx'));
const AdminProjects       = React.lazy(() => import('./pages/Admin/Projects/AdminProjects.jsx'));
const PostsManager        = React.lazy(() => import('./pages/Admin/Blog/PostsManager.jsx'));
const ChatManager         = React.lazy(() => import('./pages/Admin/Chat/ChatManager.jsx'));
const UploadsManager      = React.lazy(() => import('./pages/Admin/Uploads/UploadsManager.jsx'));
const SettingsPanel       = React.lazy(() => import('./pages/Admin/Settings/SettingsPanel.jsx'));
const ActivityLogs        = React.lazy(() => import('./pages/Admin/Activity/ActivityLogs.jsx'));
const MaintenanceManager  = React.lazy(() => import('./pages/Admin/Maintenance/MaintenanceManager.jsx'));

// ── Loading Fallback ─────────────────────────────────────────────────
const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <LoadingSpinner size="large" />
      <p className="mt-4 font-medium text-gray-600 dark:text-gray-400">جاري تحميل النظام...</p>
    </div>
  </div>
);

// ── Protected Route ──────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return user ? children : <Navigate to="/admin/login" replace />;
};

// ── Maintenance Guard ─────────────────────────────────────────────────
// بيتحقق من الصيانة ويحول الزوار لصفحة الصيانة
// الأدمن المسجل دخوله يعدي بدون مشاكل

const MaintenanceGuard = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [maintenanceState, setMaintenanceState] = useState({
    checked:   false,
    isEnabled: false,
  });

  // الصفحات اللي مش محتاجة تتحقق من الصيانة
  const exemptPaths = [
    '/maintenance',
    '/admin',        // كل صفحات الأدمن
    '/admin-gate',
  ];

  const isExempt = exemptPaths.some(p =>
    location.pathname === p || location.pathname.startsWith(p)
  );

  const checkMaintenance = useCallback(async () => {
    // لو الأدمن مسجل دخول → ما نتحققش
    if (user) {
      setMaintenanceState({ checked: true, isEnabled: false });
      return;
    }

    // لو في صفحة مستثناة → ما نتحققش
    if (isExempt) {
      setMaintenanceState({ checked: true, isEnabled: false });
      return;
    }

    try {
      const response = await publicChatApi.get('/maintenance/status');
      const data = response.data;

      setMaintenanceState({
        checked:   true,
        isEnabled: data.success ? (data.isEnabled || false) : false,
      });
    } catch (error) {
      // لو فيه خطأ → نكمل عادي (fail open)
      setMaintenanceState({ checked: true, isEnabled: false });
    }
  }, [user, isExempt]);

  useEffect(() => {
    // انتظر لحد ما الـ auth يخلص loading
    if (!authLoading) {
      checkMaintenance();
    }
  }, [authLoading, checkMaintenance, location.pathname]);

  // تحقق تلقائي كل دقيقة لو مش أدمن
  useEffect(() => {
    if (user || isExempt) return;

    const interval = setInterval(checkMaintenance, 60 * 1000);
    return () => clearInterval(interval);
  }, [user, isExempt, checkMaintenance]);

  // لو لسه بيتحقق → اعرض loading
  if (!maintenanceState.checked || authLoading) {
    return <LoadingFallback />;
  }

  // لو الصيانة شغالة والمستخدم مش أدمن → حوّله لصفحة الصيانة
  if (maintenanceState.isEnabled && !user && !isExempt) {
    return <Navigate to="/maintenance" replace />;
  }

  return children;
};

// ── App Component ─────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <div className="min-h-screen bg-gray-50 text-gray-900 transition-colors duration-200 dark:bg-gray-900 dark:text-white App">
              <Suspense fallback={<LoadingFallback />}>
                <MaintenanceGuard>
                  <Routes>
                    {/* Role Selection */}
                    <Route path="/" element={<RoleSelectPage />} />

                    {/* Public Routes */}
                    <Route path="/visitor" element={<PublicLayout />}>
                      <Route index element={<HomePage />} />
                      <Route path="about"            element={<AboutPage />} />
                      <Route path="projects"         element={<ProjectsPage />} />
                      <Route path="projects/:id"     element={<ProjectDetailsPage />} />
                      <Route path="blog"             element={<BlogPage />} />
                      <Route path="blog/:slug"       element={<BlogPostPage />} />
                      <Route path="contact"          element={<ContactPage />} />
                    </Route>

                    {/* Maintenance Page */}
                    <Route path="/maintenance" element={<MaintenancePage />} />

                    {/* Admin Gate */}
                    <Route path="/admin-gate" element={<AdminGatePage />} />

                    {/* Admin Login */}
                    <Route path="/admin/login" element={<AuthLayout />}>
                      <Route index element={<AdminLoginPage />} />
                    </Route>

                    {/* Admin Routes - محمية بالتوكن */}
                    <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                      <Route index                element={<Navigate to="/admin/dashboard" replace />} />
                      <Route path="dashboard"    element={<DashboardOverview />} />
                      <Route path="profile"      element={<Profile />} />
                      <Route path="users"        element={<UsersManager />} />
                      <Route path="projects"     element={<AdminProjects />} />
                      <Route path="posts"        element={<PostsManager />} />
                      <Route path="chat"         element={<ChatManager />} />
                      <Route path="uploads"      element={<UploadsManager />} />
                      <Route path="settings"     element={<SettingsPanel />} />
                      <Route path="activity"     element={<ActivityLogs />} />
                      <Route path="maintenance"  element={<MaintenanceManager />} />
                    </Route>

                    {/* 404 */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </MaintenanceGuard>
              </Suspense>

              {/* Chat Widget - للزوار فقط */}
              <ChatWidget />

              {/* Toast */}
              <Toaster
                position="top-right"
                gutter={8}
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                    padding: '16px',
                    borderRadius: '8px',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: { primary: '#4ade80', secondary: '#fff' },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: { primary: '#ef4444', secondary: '#fff' },
                  },
                }}
              />
            </div>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;